// src/modules/app-views/products/pages/Init/modals/ProductsParametersModal/tabs/WarehousesTab.jsx
// *******************************************************************************
// WAREHOUSES TAB (HIERARCHICAL)
// *******************************************************************************
// Manages stock warehouses/locations as a tree hierarchy.
// Structure: Warehouse → Aisle → Rack → Level → Bin
// Same Tree pattern as CategoriesTab.
// Stored in parameters.modules.products.warehouses.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Tree, Button, Flex, Space, Popconfirm, Typography, Tooltip } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InboxOutlined,
  EnvironmentOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import WarehouseFormModal from './WarehouseFormModal';

// Palette of distinct colors for root warehouses
const WAREHOUSE_COLORS = [
  '#1677ff', '#52c41a', '#faad14', '#eb2f96', '#722ed1',
  '#13c2c2', '#fa541c', '#2f54eb', '#a0d911', '#f5222d',
  '#597ef7', '#36cfc9', '#ff7a45', '#9254de', '#ffc53d',
];

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const WarehousesTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [parentNode, setParentNode] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [mousePosition, setMousePosition] = useState(null);

  // Initialize from products parameters — auto-assign colors to legacy root nodes
  const [warehouses, setWarehouses] = useState(() => {
    const raw = appService.getModulePreferences({
      moduleId: 'products',
      category: 'warehouses',
    });
    return assignMissingColors(normalizeToArray(raw));
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAddRoot = useCallback(e => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setParentNode(null);
    setShowModal(true);
  }, []);

  const handleAddChild = useCallback((node, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setParentNode(node);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((node, e, isRoot) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord({ ...node, _isRoot: isRoot });
    setParentNode(null);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(
    async formData => {
      const { code, name, description, sortOrder, color, parentUid, isDefault } = formData;

      // Check for duplicates at the same level
      const siblings = parentUid
        ? findNodeByKey(warehouses, parentUid)?.children || []
        : warehouses;

      const isDuplicate = siblings.some(
        w =>
          w.uid !== editingRecord?.uid &&
          (w.code?.toLowerCase() === code?.toLowerCase() ||
            w.name?.toLowerCase() === name?.toLowerCase()),
      );

      if (isDuplicate) {
        appService.message({ flag: 'w', text: 'item_already_exists' });
        return;
      }

      setLoading(true);
      try {
        let updated;

        if (editingRecord) {
          // Editing existing node — use _isRoot flag set during handleEdit
          const isRootNode = !!editingRecord._isRoot;
          const updates = { code, name, description };
          if (isRootNode) {
            updates.color = color;
            updates.isDefault = isDefault;
          } else {
            updates.sortOrder = sortOrder;
          }
          updated = updateNodeInTree(warehouses, editingRecord.uid, updates);
          // If setting as default, revoke from all other root nodes
          if (isRootNode && isDefault) {
            updated = revokeDefaultExcept(updated, editingRecord.uid);
          }
        } else if (parentUid) {
          // Adding child node
          const newNode = {
            uid: generateUidV4(),
            code,
            name,
            description,
            sortOrder,
            children: [],
          };
          updated = addChildToTree(warehouses, parentUid, newNode);

          // Expand parent to show new child
          if (!expandedKeys.includes(parentUid)) {
            setExpandedKeys([...expandedKeys, parentUid]);
          }
        } else {
          // Adding root node — color comes from form (user picked or suggested)
          const setAsDefault = warehouses.length === 0 || isDefault;
          const newUid = generateUidV4();

          updated = [
            ...warehouses,
            {
              uid: newUid,
              code,
              name,
              description,
              color: color || '#1677ff',
              children: [],
              isDefault: setAsDefault,
            },
          ];

          // Revoke default from others if this one is default
          if (setAsDefault) {
            updated = revokeDefaultExcept(updated, newUid);
          }
        }

        await appService.updateModulePreferences({
          moduleId: 'products',
          category: 'warehouses',
          data: updated,
        });

        setWarehouses(updated);
        appService.message({ flag: 's', text: 'saved_successfully' });
        setShowModal(false);
        setEditingRecord(null);
        setParentNode(null);
      } catch (error) {
        console.error('[WarehousesTab] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [warehouses, editingRecord, expandedKeys, setLoading],
  );

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setEditingRecord(null);
    setParentNode(null);
  }, []);

  const handleDelete = useCallback(
    async nodeUid => {
      setLoading(true);
      try {
        const updated = removeNodeFromTree(warehouses, nodeUid);

        await appService.updateModulePreferences({
          moduleId: 'products',
          category: 'warehouses',
          data: updated,
        });

        setWarehouses(updated);
        appService.message({ flag: 's', text: 'removed_successfully' });
      } catch (error) {
        console.error('[WarehousesTab] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [warehouses, setLoading],
  );

  const handleExpand = useCallback(keys => {
    setExpandedKeys(keys);
  }, []);

  const handleExpandAll = useCallback(() => {
    setExpandedKeys(getAllNodeKeys(warehouses));
  }, [warehouses]);

  const handleCollapseAll = useCallback(() => {
    setExpandedKeys([]);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const treeData = useMemo(
    () => buildTreeData(warehouses, { handleEdit, handleDelete, handleAddChild, loading }),
    [warehouses, handleEdit, handleDelete, handleAddChild, loading],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical>
      <div style={{ maxHeight: 'calc(80vh - 240px)', overflow: 'auto' }}>
        {warehouses.length === 0 ? (
          <Flex justify="center" align="center" style={{ minHeight: 120 }}>
            <Text type="secondary">{translateX('no_data')}</Text>
          </Flex>
        ) : (
          <Tree
            treeData={treeData}
            expandedKeys={expandedKeys}
            onExpand={handleExpand}
            showLine={{ showLeafIcon: false }}
            showIcon
            icon={props =>
              props.expanded ? <InboxOutlined /> : props.isLeaf ? <EnvironmentOutlined /> : <InboxOutlined />
            }
            selectable={false}
            blockNode
            className="warehouses-tree"
          />
        )}
      </div>

      <Flex justify="space-between" align="center" className="mt-3" style={{ flexShrink: 0 }}>
        <Space size="small">
          <Tooltip title={translateX('expand_all')}>
            <Button
              type="text"
              icon={<DownOutlined />}
              onClick={handleExpandAll}
              disabled={loading || warehouses.length === 0}
            />
          </Tooltip>
          <Tooltip title={translateX('collapse_all')}>
            <Button
              type="text"
              icon={<UpOutlined />}
              onClick={handleCollapseAll}
              disabled={loading || warehouses.length === 0}
            />
          </Tooltip>
        </Space>

        <Button icon={<PlusOutlined />} onClick={handleAddRoot} disabled={loading}>
          {translateX('add_warehouse')}
        </Button>
      </Flex>

      <WarehouseFormModal
        open={showModal}
        onCancel={handleCancel}
        onSave={handleSave}
        initialData={editingRecord}
        parentNode={parentNode}
        isRootLevel={editingRecord ? !!editingRecord._isRoot : !parentNode}
        usedColors={warehouses.map(w => w.color).filter(Boolean)}
        nextSortOrder={computeNextSortOrder(warehouses, parentNode)}
        loading={loading}
        mousePosition={mousePosition}
      />

      <style>{`
        .warehouses-tree .ant-tree-treenode {
          align-items: center;
          padding: 4px 0;
        }
        .warehouses-tree .ant-tree-node-content-wrapper {
          display: flex;
          align-items: center;
          flex: 1;
          min-width: 0;
        }
        .warehouses-tree .ant-tree-title {
          flex: 1;
          min-width: 0;
        }
        .warehouses-tree .ant-tree-iconEle {
          flex-shrink: 0;
        }
      `}</style>
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default WarehousesTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************

function buildTreeData(nodes, handlers, isRootLevel = true) {
  const { handleEdit, handleDelete, handleAddChild, loading } = handlers;
  const arr = normalizeToArray(nodes);

  // Sort children (non-root) by sortOrder
  const sorted = isRootLevel
    ? arr
    : [...arr].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  return sorted.map(node => {
    const nodeChildren = normalizeToArray(node.children);
    const hasChildren = nodeChildren.length > 0;
    const codeLabel = node.code ? `[${node.code}] ` : '';

    const title = (
      <Flex align="center" justify="space-between" style={{ width: '100%', minWidth: 0 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {/* Color dot only for root warehouses */}
          {isRootLevel && node.color && (
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: node.color,
                marginRight: 6,
                verticalAlign: 'middle',
              }}
            />
          )}
          {/* Sort order prefix for addressing (children) */}
          {!isRootLevel && node.sortOrder != null && (
            <Text type="secondary" style={{ marginRight: 6, fontSize: 11 }}>
              #{node.sortOrder}
            </Text>
          )}
          <Text strong style={{ marginRight: 4 }}>{codeLabel}</Text>
          {node.name}
          {isRootLevel && node.isDefault && (
            <Text type="secondary" style={{ marginLeft: 8, fontSize: 11 }}>
              ({translateX('default')})
            </Text>
          )}
        </span>
        <Space size={0} style={{ flexShrink: 0, marginLeft: 8 }}>
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            onClick={e => {
              e.stopPropagation();
              handleAddChild(node, e);
            }}
            disabled={loading}
            title={translateX('add_location')}
          />
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={e => {
              e.stopPropagation();
              handleEdit(node, e, isRootLevel);
            }}
            disabled={loading}
          />
          <Popconfirm
            title={
              hasChildren
                ? translateX('confirm_delete_with_children')
                : translateX('confirm_delete')
            }
            onConfirm={() => handleDelete(node.uid)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={e => e.stopPropagation()}
              disabled={loading}
            />
          </Popconfirm>
        </Space>
      </Flex>
    );

    // Children are always non-root (addressing)
    const children = hasChildren ? buildTreeData(nodeChildren, handlers, false) : undefined;

    return {
      key: node.uid,
      title,
      children,
      isLeaf: !hasChildren,
    };
  });
}

function findNodeByKey(nodes, nodeId) {
  const arr = normalizeToArray(nodes);
  for (const node of arr) {
    if (node.uid === nodeId) return node;
    if (node.children) {
      const found = findNodeByKey(node.children, nodeId);
      if (found) return found;
    }
  }
  return null;
}

function addChildToTree(nodes, parentId, newChild) {
  const arr = normalizeToArray(nodes);
  return arr.map(node => {
    if (node.uid === parentId) {
      return {
        ...node,
        children: [...normalizeToArray(node.children), newChild],
      };
    }
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: addChildToTree(node.children, parentId, newChild),
      };
    }
    return node;
  });
}

function updateNodeInTree(nodes, nodeId, updates) {
  const arr = normalizeToArray(nodes);
  return arr.map(node => {
    if (node.uid === nodeId) {
      return { ...node, ...updates };
    }
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: updateNodeInTree(node.children, nodeId, updates),
      };
    }
    return node;
  });
}

function removeNodeFromTree(nodes, nodeId) {
  const arr = normalizeToArray(nodes);
  return arr
    .filter(node => node.uid !== nodeId)
    .map(node => {
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: removeNodeFromTree(node.children, nodeId),
        };
      }
      return node;
    });
}

function getAllNodeKeys(nodes) {
  const arr = normalizeToArray(nodes);
  let keys = [];
  for (const node of arr) {
    keys.push(node.uid);
    if (node.children && node.children.length > 0) {
      keys = keys.concat(getAllNodeKeys(node.children));
    }
  }
  return keys;
}

function normalizeToArray(data) {
  if (Array.isArray(data)) return data;
  if (data == null) return [];

  if (typeof data === 'object') {
    const keys = Object.keys(data);
    const isNumericKeys = keys.length > 0 && keys.every(k => /^\d+$/.test(k));
    if (isNumericKeys) {
      return keys.sort((a, b) => Number(a) - Number(b)).map(k => {
        const item = data[k];
        if (item && item.children) {
          return { ...item, children: normalizeToArray(item.children) };
        }
        return item;
      });
    }
  }

  return [];
}

/**
 * Assign colors to root warehouses that don't have one (legacy data).
 * Persists the update to backend so it only happens once.
 */
function assignMissingColors(nodes) {
  const needsColor = nodes.some(n => !n.color);
  if (!needsColor) return nodes;

  const usedColors = nodes.map(n => n.color).filter(Boolean);
  const updated = nodes.map(node => {
    if (node.color) return node;

    const available = WAREHOUSE_COLORS.filter(c => !usedColors.includes(c));
    const color = available.length > 0
      ? available[Math.floor(Math.random() * available.length)]
      : WAREHOUSE_COLORS[Math.floor(Math.random() * WAREHOUSE_COLORS.length)];

    usedColors.push(color);
    return { ...node, color };
  });

  // Persist silently so next load already has colors
  appService.updateModulePreferences({
    moduleId: 'products',
    category: 'warehouses',
    data: updated,
  });

  return updated;
}

/**
 * Revoke isDefault from all root warehouses except the given uid.
 */
function revokeDefaultExcept(nodes, keepUid) {
  return nodes.map(node =>
    node.uid === keepUid ? node : { ...node, isDefault: false },
  );
}

/**
 * Compute the next sortOrder for a new node at the given level.
 * Returns max(siblings sortOrder) + 1, or 1 if no siblings.
 */
function computeNextSortOrder(warehouses, parentNode) {
  const siblings = parentNode
    ? findNodeByKey(warehouses, parentNode.uid)?.children || []
    : warehouses;

  if (siblings.length === 0) return 1;

  const maxOrder = Math.max(...siblings.map(s => s.sortOrder || 0));
  return maxOrder + 1;
}
